/* HS!/JPD CUSTOMIZER — MVP
 * Collects hardware/thread selections as visible line item properties,
 * adds priced embellishment variants alongside the base garment, links
 * everything with a shared CUSTOMIZATION REF, and stamps the entity tag
 * on every line. No rendering engine — real photography + swatches only.
 */
(function () {
  'use strict';

  function money(cents, format) {
    var amount = (cents / 100).toFixed(2);
    if (format && format.indexOf('{{') !== -1) {
      // Character classes, not backslash escapes — theme JS stays free of
      // backslash literals so GraphQL theme-file deploys never mangle them.
      return format
        .replace(/[{][{][ ]*amount[ ]*[}][}]/, Number(amount).toLocaleString('en-US', { minimumFractionDigits: 2 }))
        .replace(/[{][{][ ]*amount_no_decimals[ ]*[}][}]/, Math.round(cents / 100).toLocaleString('en-US'));
    }
    return '$' + amount;
  }

  // The 34 zones are the real, seam-measured contract (section THE FLAT in
  // START_HERE.md) and the ticket generator's input. Nothing here changes that
  // geometry.
  //
  // THERE IS NO ZOOM. There used to be an 8-tile "zone group" picker that
  // retargeted the root viewBox, and it was broken in a way markup could not
  // see: it computed the zoom box with getCTM(), which on a viewBox'd SVG
  // returns VIEWPORT coordinates, then wrote those straight back into the
  // viewBox, which expects USER coordinates. Every one of the eight tiles
  // zoomed to a box that did not contain its own zones, so a customer could
  // not reliably place a pin at all.
  //
  // It is not replaced with a fixed zoom, because zoom was solving the wrong
  // problem. At 375px the flat renders about 309px wide showing front AND back,
  // so 25 of the 27 body zones fall under the 44px touch minimum anyway (the
  // coin pocket is 8.2px). Instead, a tap ANYWHERE on the flat snaps to the
  // nearest allowed zone via hitZone(), which resolves through getScreenCTM()
  // and was always correct. You no longer have to hit an 8px rectangle; you
  // have to be nearer to it than to anything else you are allowed to pick.

  function initSection(root) {
    var cfgEl = root.querySelector('[data-hsc-config]');
    if (!cfgEl) return;
    var cfg = JSON.parse(cfgEl.textContent);
    // When the section also carries a builder config, hs-builder.js owns the
    // cart, the summary and the CTA. This file then provides ONLY the placement
    // picker — binding add-to-cart here as well would submit the order twice.
    // ALTERATIONS does the same thing for the same reason: hs-alterations.js owns
    // that cart because its lines carry quantities and per-service options this
    // file knows nothing about, and it has no base garment variant at all.
    // Purely additive — a page with neither attribute behaves exactly as before.
    var placementOnly = !!root.querySelector('[data-hsb-config], [data-hsa-config]');

    // Instrumentation. This engine drives three different surfaces, so the
    // funnel has to be told which one it is on or every number would be summed
    // into one meaningless total. On the builder and the alterations bench this
    // engine owns ONLY placement, so it reports only that step - their own
    // engines own the steps either side of it.
    var trackSurface = root.querySelector('[data-hsa-config]') ? 'alterations'
      : (root.querySelector('[data-hsb-config]') ? 'build' : 'customize');
    var track = window.HSTrack ? window.HSTrack.for(trackSurface) : null;

    var variantInput = root.querySelector('[data-hsc-variant]');
    var addonInputs = Array.prototype.slice.call(root.querySelectorAll('[data-hsc-addon]'));
    var addBtn = root.querySelector('[data-hsc-add]');
    var errEl = root.querySelector('[data-hsc-error]');
    var sumBase = root.querySelector('[data-hsc-sum-base]');
    var sumAddons = root.querySelector('[data-hsc-sum-addons]');
    var sumTotal = root.querySelector('[data-hsc-sum-total]');
    var sumLead = root.querySelector('[data-hsc-sum-lead]');

    // thumbnails
    Array.prototype.forEach.call(root.querySelectorAll('.hsc__thumb'), function (btn) {
      btn.addEventListener('click', function () {
        var img = root.querySelector('.hsc__img');
        if (img && img.tagName === 'IMG') img.src = btn.getAttribute('data-full');
      });
    });

    function basePriceCents() {
      if (variantInput && variantInput.tagName === 'SELECT') {
        var opt = variantInput.options[variantInput.selectedIndex];
        return parseInt(opt.getAttribute('data-price'), 10) || cfg.basePriceCents;
      }
      return cfg.basePriceCents;
    }

    function selectedAddons() {
      return addonInputs.filter(function (i) { return i.checked; });
    }

    function updateSummary() {
      syncDerived();
      var base = basePriceCents();
      var addons = selectedAddons();
      var addonCents = addons.reduce(function (s, i) { return s + (parseInt(i.getAttribute('data-price'), 10) || 0); }, 0);
      // Lead time: base days + the longest single add-on (work overlaps;
      // the slowest process gates the unit). Stated as an estimate.
      var maxLead = addons.reduce(function (m, i) { return Math.max(m, parseInt(i.getAttribute('data-lead'), 10) || 0); }, 0);
      var lead = (parseInt(cfg.baseLeadDays, 10) || 5) + maxLead;

      if (sumBase) sumBase.textContent = money(base, cfg.moneyFormat);
      if (sumAddons) sumAddons.textContent = money(addonCents, cfg.moneyFormat);
      if (sumTotal) sumTotal.textContent = money(base + addonCents, cfg.moneyFormat);
      if (sumLead) sumLead.textContent = lead + ' BUSINESS DAYS';
      return lead;
    }

    function showError(msg) {
      if (!errEl) return;
      errEl.textContent = msg;
      errEl.hidden = false;
      if (track) track.event('hs_customize_blocked', { reason: msg });
    }
    // Same rule as the builder and the alterations bench: a fixed fault must stop
    // being reported. Clears the text too, so nothing stale is left for a screen
    // reader or for the next thing that unhides the region.
    function clearError() {
      if (!errEl) return;
      errEl.textContent = '';
      errEl.hidden = true;
    }

    // --- Placement on the flat sketch --------------------------------------
    // placement_config metafield on the add-on variant dictates the rules:
    //   { "zones": ["F-PKT-L", ...], "limit": 2 }  → only those zones take a
    //   tap, and placement is REQUIRED before add-to-cart.
    //   No metafield → every zone allowed, placement optional (notes only is
    //   fine) — legacy behavior for un-configured add-ons.
    var flatWrap = root.querySelector('[data-hsc-flat]');
    var flatActive = root.querySelector('[data-hsc-flat-active]');
    var flatZones = flatWrap ? Array.prototype.slice.call(flatWrap.querySelectorAll('[data-hsf-zone]')) : [];
    var previewTray = root.querySelector('[data-hsc-previews]');
    var placements = {}; // addon variant id -> [zone ids, oldest first]
    var activePlaceId = null;

    function placementCfg(input) {
      var raw = input.getAttribute('data-placement');
      if (!raw || raw === 'null') return null;
      try { return JSON.parse(raw); } catch (e) { return null; }
    }
    function allowedZones(input) {
      var pc = placementCfg(input);
      if (pc && pc.zones && pc.zones.length) return pc.zones;
      return flatZones.map(function (z) { return z.getAttribute('data-hsf-zone'); });
    }
    function zoneLimit(input) {
      var pc = placementCfg(input);
      return (pc && parseInt(pc.limit, 10)) || 1;
    }
    function zoneLabel(id) {
      var el = flatWrap && flatWrap.querySelector('[data-hsf-zone="' + id + '"]');
      return el ? el.getAttribute('data-zone-label') : id;
    }
    function addonById(id) {
      return addonInputs.filter(function (i) { return i.value === String(id); })[0] || null;
    }

    // --- Automatic surcharges + per-garment availability ---------------------
    // Some work is not a technique the customer chooses, it is a CONSEQUENCE of
    // where they put one. Chainstitch on a back pocket means the pocket comes off
    // and goes back on so it still holds a wallet, and that is real bench time.
    // A surcharge variant declares itself in its own placement_config:
    //
    //   "surcharge_for":   ["CHAINSTITCH EMBROIDERY"]   trigger add-on titles
    //   "surcharge_zones": ["B-PKT-L", "B-PKT-R"]       zones that trigger it;
    //                                                   defaults to "zones"
    //
    // It is never tickable by hand. The engine checks it, hands it the SAME zones
    // the trigger was placed on so the ticket names which pocket, and unchecks it
    // the moment the trigger moves off. Because it then rides the ordinary
    // checked-add-on path, pricing, lead time, the cart line, the placement
    // properties and the factory ticket all work with no special case anywhere
    // else — including in hs-builder.js, which shares these same DOM inputs.
    function surchargeCfg(input) {
      var pc = placementCfg(input);
      return pc && pc.surcharge_for && pc.surcharge_for.length ? pc : null;
    }
    // Zones the trigger actually occupies that this surcharge cares about.
    // FREESTYLE deliberately does NOT trigger one: the shop has not chosen a spot
    // yet, and billing for pocket work that may never happen is worse than adding
    // it by hand when it does. effectiveZones() already returns [] for freestyle.
    function surchargeZones(pc) {
      var want = pc.surcharge_zones && pc.surcharge_zones.length ? pc.surcharge_zones : (pc.zones || []);
      var hit = [];
      addonInputs.forEach(function (t) {
        if (!t.checked || pc.surcharge_for.indexOf(t.getAttribute('data-title')) === -1) return;
        effectiveZones(t.value).forEach(function (zid) {
          if (want.indexOf(zid) !== -1 && hit.indexOf(zid) === -1) hit.push(zid);
        });
      });
      return hit;
    }
    // A zone only counts if it is in the flat the customer is LOOKING at. Every
    // garment flat sits in the DOM at once and the unselected ones carry hidden,
    // so a query across the whole wrapper finds a jean pocket while a tote is on
    // screen. A page with a single flat has no face wrapper and is always live.
    function zoneIsLive(zid) {
      var z = zoneNode(zid);
      if (!z) return false;
      var face = z.closest ? z.closest('[data-hsc-flat-for], [data-hsb-flat-for]') : null;
      return !face || !face.hidden;
    }
    // An add-on whose every configured zone belongs to some OTHER garment cannot
    // be performed on this one — a tote has no back pocket. Hide it rather than
    // offer it and then refuse the order with "tap the flat to place", which
    // reads as a broken page rather than an unavailable option.
    function availableHere(input) {
      if (!flatWrap) return true;              // no flat to reason about
      var pc = placementCfg(input);
      if (!pc || !pc.zones || !pc.zones.length) return true;   // unrestricted
      for (var i = 0; i < pc.zones.length; i++) {
        if (zoneIsLive(pc.zones[i])) return true;
      }
      return false;
    }
    function addonRow(input) {
      return input.closest ? input.closest('.hsc__addon') : null;
    }
    // Derived state — what the customer's choices IMPLY, as against what they
    // ticked. Idempotent and cheap, so it runs at the top of both updateSummary
    // and updateFlat instead of being threaded through every event handler.
    // Re-entry guard. syncDerived has to refresh the price panel when it flips a
    // line — a surcharge that is in the cart but not in the total is the worst of
    // both worlds — but updateSummary calls syncDerived on the way in, so without
    // this the pair recurse.
    var syncing = false;
    function syncDerived() {
      if (syncing) return false;
      syncing = true;
      refreshScale();          // the visible flat may have just changed
      var changed = false;
      addonInputs.forEach(function (input) {
        var pc = surchargeCfg(input);
        var live = availableHere(input);
        var zs = (pc && live) ? surchargeZones(pc) : [];
        var on;
        if (pc) {
          // Auto only. A customer must not be able to tick a consequence, nor to
          // clear one they have earned.
          input.disabled = true;
          on = zs.length > 0;
          if (on) placements[input.value] = zs.slice();
        } else {
          on = input.checked && live;
          // Drop pins belonging to a garment the customer has switched away from.
          // Left alone they still reach the cart, so a tote's ticket would name a
          // jean thigh. Only prune when there IS a flat to judge against.
          if (flatWrap && placements[input.value]) {
            var had = placements[input.value];
            var kept = had.filter(function (zid) { return zoneIsLive(zid); });
            if (kept.length !== had.length) {
              if (kept.length) placements[input.value] = kept;
              else delete placements[input.value];
              changed = true;
            }
          }
        }
        if (!on) delete placements[input.value];
        if (input.checked !== on) { input.checked = on; changed = true; }
        var row = addonRow(input);
        if (row) row.hidden = !live || (!!pc && !on);
      });
      // The guard stays UP across these refreshes: updateSummary calls back into
      // this function on the way in, and clearing the flag first would recurse.
      try {
        if (changed) {
          // This page's own panel. Nothing else recomputes it after a tap on the
          // flat, so an automatic surcharge would sit in the cart unpriced.
          updateSummary();
          // And hs-builder.js owns the panel on the JPD CUSTOM page, where its
          // change listener is registered before this file's. Same bridge idea as
          // root.hsPlacementFor, pointing the other way.
          if (typeof root.hsRefreshSummary === 'function') root.hsRefreshSummary();
        }
      } finally {
        syncing = false;
      }
      return changed;
    }

    function activeAddon() {
      var a = activePlaceId ? addonById(activePlaceId) : null;
      if (!a || !a.checked) {
        // A surcharge is placed by the engine, never by the customer, so it must
        // not become the thing the next tap on the flat would move.
        a = selectedAddons().filter(function (i) { return !surchargeCfg(i); })[0] || null;
        activePlaceId = a ? a.value : null;
      }
      return a;
    }

    function updateFlat() {
      syncDerived();
      if (!flatWrap) return;
      var checked = selectedAddons();
      // The flat is the hero image for this flow — the customer's own garment has
      // no product photo, so hiding the drawing until an add-on was ticked left the
      // page with nothing to look at. Always visible; the zone highlighting alone
      // communicates what can be placed.
      flatWrap.hidden = false;
      var active = activeAddon();
      var allow = active ? allowedZones(active) : [];
      var mine = active ? effectiveZones(active.value) : [];
      flatZones.forEach(function (z) {
        var id = z.getAttribute('data-hsf-zone');
        var others = checked.some(function (i) {
          return i !== active && effectiveZones(i.value).indexOf(id) !== -1;
        });
        z.classList.toggle('is-allowed', !!active && allow.indexOf(id) !== -1);
        z.classList.toggle('is-on', mine.indexOf(id) !== -1);
        z.classList.toggle('is-other', others && mine.indexOf(id) === -1);
      });
      if (flatActive) {
        flatActive.textContent = active
          ? 'PLACING: ' + active.getAttribute('data-title') + ' — UP TO ' + zoneLimit(active) + ' ZONE(S)'
            + (mine.length ? ' — ' + mine.map(zoneLabel).join(' + ') : '')
          : '';
      }
      addonInputs.forEach(function (input) {
        var row = root.querySelector('[data-hsc-addon-place="' + input.value + '"]');
        if (!row) return;
        // A surcharge gets no PLACE IT button and no removable chips: the customer
        // did not choose where it goes and must not be able to un-place it.
        row.hidden = !input.checked || !!surchargeCfg(input);
        var btn = row.querySelector('[data-hsc-place-btn]');
        if (btn) btn.classList.toggle('is-active', active === input);
        var chips = row.querySelector('[data-hsc-chips]');
        if (chips) {
          chips.innerHTML = effectiveZones(input.value).map(function (zid) {
            return '<button type="button" class="hsc__chip" data-hsc-chip-zone="' + zid + '" data-hsc-chip-addon="' + input.value + '">' + zoneLabel(zid) + ' ×</button>';
          }).join('');
        }
      });
      updatePreviews();
    }

    // --- PNG preview overlays ----------------------------------------------
    // preview_image metafield on the add-on variant (emitted as data-preview)
    // is a real photo of the technique on denim. Two surfaces:
    //   1. the flat — the photo drops into each marked zone rect, so the flat
    //      reads like the ticket will, with the actual work in place;
    //   2. a tray under the hero photo — one card per checked add-on with its
    //      photo and placement labels.
    // No metafield → both surfaces skip that add-on (zones alone still work).
    var SVG_NS = 'http://www.w3.org/2000/svg';
    var XLINK_NS = 'http://www.w3.org/1999/xlink';

    function previewUrl(input) {
      return input.getAttribute('data-preview') || '';
    }
    function esc(s) {
      return String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/"/g, '&quot;');
    }

    // --- Drag-able placement pins ------------------------------------------
    // placements[] still holds the zone ids (that is the contract with
    // placement_config and the ticket). offsets[] carries the exact point
    // inside the zone so the customer can slide the work to where they
    // actually want it. A pin dragged outside every allowed zone is clamped
    // back to the nearest one — freedom, but only within what we can sew.
    var flatSvg = flatWrap ? flatWrap.querySelector('svg') : null;
    var viewW = 430;
    var PIN = viewW * 0.032; // half-size of a pin, scales with the visible flat

    // Every garment flat sits in the DOM at once, so "the first svg" is the jean
    // whichever garment is on screen. Its viewBox is 1800 units wide and the
    // tote's is 2820, so a pin sized from the wrong one renders about a third too
    // small and drags the tap tolerance down with it. Track the visible face.
    function visibleSvg() {
      if (!flatWrap) return null;
      var all = flatWrap.querySelectorAll('svg');
      for (var i = 0; i < all.length; i++) {
        var face = all[i].closest ? all[i].closest('[data-hsc-flat-for], [data-hsb-flat-for]') : null;
        if (!face || !face.hidden) return all[i];
      }
      return all[0] || null;
    }
    function refreshScale() {
      var svg = visibleSvg();
      if (svg) flatSvg = svg;
      if (flatSvg) {
        var vb = (flatSvg.getAttribute('viewBox') || '').split(/[ ,]+/);
        if (vb.length === 4 && parseFloat(vb[2])) viewW = parseFloat(vb[2]);
      }
      PIN = viewW * 0.032;
    }
    refreshScale();
    var offsets = {};        // addonId -> { zoneId: {x,y} } in that zone's <g> coords

    // A per-add-on "let us decide" toggle.
    function isFreestyle(addonId) {
      var el = root.querySelector('[data-hsc-freestyle="' + addonId + '"]');
      return !!(el && el.checked);
    }
    // The zones that actually count. FREESTYLE hands the decision to the shop,
    // so it must also erase the marks on screen — a flat still showing pins while
    // the ticket says SHOP CHOOSES is the preview lying about the garment.
    // Everything that draws or submits placement goes through here.
    function effectiveZones(addonId) {
      return isFreestyle(addonId) ? [] : (placements[addonId] || []);
    }

    function zoneNode(id) {
      return flatWrap ? flatWrap.querySelector('[data-hsf-zone="' + id + '"]') : null;
    }
    function zoneBox(z) {
      return {
        x: parseFloat(z.getAttribute('x')), y: parseFloat(z.getAttribute('y')),
        w: parseFloat(z.getAttribute('width')), h: parseFloat(z.getAttribute('height'))
      };
    }
    function pointAt(addonId, zid) {
      var m = offsets[addonId];
      if (m && m[zid]) return m[zid];
      var z = zoneNode(zid);
      if (!z) return null;
      var b = zoneBox(z);
      return { x: b.x + b.w / 2, y: b.y + b.h / 2 };
    }
    function setPoint(addonId, zid, x, y) {
      (offsets[addonId] || (offsets[addonId] = {}))[zid] = { x: x, y: y };
    }
    // Client pixel -> user coords of a specific <g>, so the front/back
    // transforms are honoured without hard-coding them.
    function toLocal(gEl, clientX, clientY) {
      if (!flatSvg || !flatSvg.createSVGPoint) return null;
      var ctm = gEl.getScreenCTM();
      if (!ctm) return null;
      var p = flatSvg.createSVGPoint();
      p.x = clientX; p.y = clientY;
      return p.matrixTransform(ctm.inverse());
    }
    // Nearest allowed zone plus the point clamped inside it.
    function hitZone(addon, clientX, clientY) {
      var best = null, bestD = Infinity, bestArea = Infinity;
      allowedZones(addon).forEach(function (zid) {
        var z = zoneNode(zid);
        if (!z) return;
        var p = toLocal(z.parentNode, clientX, clientY);
        if (!p) return;
        var b = zoneBox(z);
        // HIT-TEST AGAINST THE REAL RECTANGLE, not a padded interior.
        // pad exists so a pin GRAPHIC stays inside its zone. Using it for the
        // distance test shrank what counted as "inside": the coin pocket is 48
        // units tall against a pin radius of 57.6, so its hittable interior
        // collapsed to a zero-height line and the front pocket it sits inside
        // won every tap at distance 0. An 8px zone was unreachable at any
        // screen size, and no amount of zooming would have fixed it.
        var ux = Math.min(Math.max(p.x, b.x), b.x + b.w);
        var uy = Math.min(Math.max(p.y, b.y), b.y + b.h);
        var d = (p.x - ux) * (p.x - ux) + (p.y - uy) * (p.y - uy);
        // The pin itself still gets clamped inside. When a zone is narrower
        // than the pin, the pad collapses to the centre, which is correct.
        var padX = Math.min(PIN, b.w / 2);
        var padY = Math.min(PIN, b.h / 2);
        var cx = Math.min(Math.max(ux, b.x + padX), b.x + b.w - padX);
        var cy = Math.min(Math.max(uy, b.y + padY), b.y + b.h - padY);
        var area = b.w * b.h;
        // SMALLEST ZONE WINS A TIE. Some zones sit inside others — the coin
        // pocket lives inside the front pocket — so a tap in the middle of the
        // coin pocket is distance-zero from BOTH. Without this the bigger zone
        // won on array order and the 8px coin pocket was unreachable at any
        // screen size, which is precisely the kind of thing markup cannot see.
        var better = (d < bestD - 0.01) ||
                     (Math.abs(d - bestD) <= 0.01 && area < bestArea);
        if (better) { bestD = d; bestArea = area; best = { zone: zid, x: cx, y: cy }; }
      });
      // SNAP HAS A LIMIT. Nearest-zone matching with no bound meant a tap in the
      // empty margin beside the drawing still dropped a pin on the waistband.
      // Two pin-radii of slack is generous for a fingertip and still refuses a
      // tap that is plainly not on the garment.
      if (best && bestD > (PIN * 2) * (PIN * 2)) return null;
      return best;
    }

    // Tap anywhere on the flat -> nearest allowed zone. This is what replaces
    // the zoom: the whole drawing is the target, not each 8px rectangle.
    // Bound on the WRAPPER so taps that land on artwork, on a gap between
    // zones, or on a zone this service is not offered for all still resolve.
    // Delegated on the WRAPPER, as the note above always said it was. Bound to a
    // single svg it only ever worked on the jean: the tote's drawing is a second
    // svg in the same wrapper, so tapping it did nothing at all and the order was
    // then refused for having no placement.
    if (flatWrap) {
      flatWrap.addEventListener('click', function (e) {
        if (!e.target.closest || !e.target.closest('svg')) return;
        // A tap that lands on an existing pin is a drag/removal, handled below.
        if (e.target.closest && e.target.closest('.hsf__pin')) return;
        var active = activeAddon();
        if (!active) return;
        var hit = hitZone(active, e.clientX, e.clientY);
        if (!hit) return;
        // Only snap when the tap is actually on the garment, not miles away in
        // the empty margin of the SVG box.
        placeAt(active, hit, true);
      });
    }

    function updatePreviews() {
      if (flatWrap) {
        Array.prototype.slice.call(flatWrap.querySelectorAll('.hsf__preview, .hsf__pin')).forEach(function (n) {
          n.parentNode.removeChild(n);
        });
      }
      var trayHtml = '';
      // A pin has to say WHICH technique it is. Without a number every imageless
      // add-on drew the same magenta dot, so a customer placing chainstitch on
      // the front and patches on the back saw two identical marks and could not
      // tell which was which - and the tray below only carded add-ons that had a
      // reference photo, so there was no legend to read either. The number is
      // per add-on, not per pin: one technique placed in two zones is the same
      // number twice, because it is the same work.
      var pinNo = 0;
      selectedAddons().forEach(function (input) {
        // A surcharge rides its trigger's zones, so drawing it would stack a
        // second pin on the same pocket and put a duplicate card in the tray. It
        // still appears in the live spec list, which is where it belongs.
        if (surchargeCfg(input)) return;
        var url = previewUrl(input);
        var zs = effectiveZones(input.value);
        var title = input.getAttribute('data-title') || '';
        pinNo += 1;
        var myNo = pinNo;

        zs.forEach(function (zid) {
          var zone = zoneNode(zid);
          if (!zone) return;
          var pt = pointAt(input.value, zid);
          if (!pt) return;
          var g = document.createElementNS(SVG_NS, 'g');
          g.setAttribute('class', 'hsf__pin');
          g.setAttribute('data-pin-addon', input.value);
          g.setAttribute('data-pin-zone', zid);
          g.setAttribute('tabindex', '0');
          g.setAttribute('role', 'button');
          g.setAttribute('aria-label', myNo + '. ' + title + ' at ' + zoneLabel(zid) + ' — drag to move');

          if (url) {
            var img = document.createElementNS(SVG_NS, 'image');
            img.setAttribute('class', 'hsf__preview');
            img.setAttribute('x', pt.x - PIN);
            img.setAttribute('y', pt.y - PIN);
            img.setAttribute('width', PIN * 2);
            img.setAttribute('height', PIN * 2);
            img.setAttribute('preserveAspectRatio', 'xMidYMid slice');
            img.setAttribute('href', url);
            img.setAttributeNS(XLINK_NS, 'xlink:href', url);
            g.appendChild(img);
          } else {
            var c = document.createElementNS(SVG_NS, 'circle');
            c.setAttribute('cx', pt.x); c.setAttribute('cy', pt.y); c.setAttribute('r', PIN);
            g.appendChild(c);
          }
          var ring = document.createElementNS(SVG_NS, 'rect');
          ring.setAttribute('class', 'hsf__pin-ring');
          ring.setAttribute('x', pt.x - PIN); ring.setAttribute('y', pt.y - PIN);
          ring.setAttribute('width', PIN * 2); ring.setAttribute('height', PIN * 2);
          ring.setAttribute('rx', PIN * 0.3);
          g.appendChild(ring);
          // The number rides ON TOP of both the dot and the photo thumbnail, so
          // it has to stay legible over an arbitrary image - hence the dark halo
          // drawn under the white glyph via paint-order in the stylesheet.
          var num = document.createElementNS(SVG_NS, 'text');
          num.setAttribute('class', 'hsf__pin-num');
          num.setAttribute('x', pt.x);
          num.setAttribute('y', pt.y);
          num.setAttribute('font-size', PIN * 1.15);
          num.textContent = String(myNo);
          g.appendChild(num);
          // Same parent <g> as the zone, so the front/back transform applies.
          zone.parentNode.appendChild(g);
        });

        // EVERY selected technique gets a card, photo or not. This used to bail
        // on a missing reference photo, which is exactly the case where the pin
        // is a bare numbered mark and the customer most needs to be told what
        // that mark means.
        trayHtml += '<figure class="hsc__preview-card">'
          + (url
            ? '<img src="' + esc(url) + '" alt="' + esc(title) + ' — reference photo" loading="lazy">'
            : '<span class="hsc__preview-badge" aria-hidden="true">' + myNo + '</span>')
          + '<figcaption><b>' + myNo + '.</b> ' + esc(title)
          + (zs.length ? ' — ' + esc(zs.map(zoneLabel).join(' + ')) : ' — NOT PLACED YET')
          + '</figcaption></figure>';
      });
      if (previewTray) {
        previewTray.innerHTML = trayHtml
          ? '<p class="hsc__previews-title">YOUR CUSTOM WORK — THE NUMBERS MATCH THE MARKS ON THE FLAT</p>' + trayHtml
          : '';
        previewTray.hidden = !trayHtml;
      }
    }

    // Tap anywhere on an allowed zone: drop (or move) the pin at that point.
    function placeAt(active, hit, toggle) {
      var list = placements[active.value] || (placements[active.value] = []);
      var at = list.indexOf(hit.zone);
      if (at !== -1 && toggle) {
        list.splice(at, 1);
        if (offsets[active.value]) delete offsets[active.value][hit.zone];
      } else if (at !== -1) {
        setPoint(active.value, hit.zone, hit.x, hit.y);
      } else {
        if (list.length >= zoneLimit(active)) {
          var dropped = list.shift(); // replace oldest
          if (offsets[active.value]) delete offsets[active.value][dropped];
        }
        list.push(hit.zone);
        setPoint(active.value, hit.zone, hit.x, hit.y);
      }
      updateFlat();
      updateMarkers();
      // Every mark, on all three surfaces, funnels through here - the builder
      // runs this same engine in placement-only mode - so this is the one place
      // the placement step can be counted without triple-instrumenting it.
      if (track) {
        track.step('placement', { technique: active.getAttribute('data-title') || '' });
      }
    }

    function tapZone(zoneRect, evt) {
      var active = activeAddon();
      if (!active) return;
      var id = zoneRect.getAttribute('data-hsf-zone');
      if (allowedZones(active).indexOf(id) === -1) {
        if (flatActive) flatActive.textContent = zoneLabel(id) + ' — NOT OFFERED FOR ' + active.getAttribute('data-title');
        return;
      }
      var hit = null;
      if (evt && typeof evt.clientX === 'number') hit = hitZone(active, evt.clientX, evt.clientY);
      if (!hit || hit.zone !== id) {
        var b = zoneBox(zoneRect);
        hit = { zone: id, x: b.x + b.w / 2, y: b.y + b.h / 2 };
      }
      placeAt(active, hit, true);
    }

    // KEYBOARD ONLY. The click path is bound once on the SVG and snaps to the
    // nearest allowed zone; binding click here as well would fire both handlers
    // for one tap and toggle the pin straight back off. Enter/Space on a focused
    // zone still places exactly on that zone, which is what a keyboard user is
    // pointing at.
    flatZones.forEach(function (z) {
      z.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); tapZone(z, null); }
      });
    });

    // Pointer drag. Pointer events cover mouse, touch and pen in one path.
    var dragging = null;
    if (flatWrap) {
      flatWrap.addEventListener('pointerdown', function (e) {
        var pin = e.target.closest ? e.target.closest('.hsf__pin') : null;
        if (!pin) return;
        var addon = addonById(pin.getAttribute('data-pin-addon'));
        if (!addon) return;
        e.preventDefault();
        e.stopPropagation();
        activePlaceId = addon.value;
        dragging = { addon: addon, zone: pin.getAttribute('data-pin-zone'), moved: false };
        pin.classList.add('is-dragging');
        if (pin.setPointerCapture) { try { pin.setPointerCapture(e.pointerId); } catch (err) {} }
        updateFlat();
      });

      flatWrap.addEventListener('pointermove', function (e) {
        if (!dragging) return;
        e.preventDefault();
        var hit = hitZone(dragging.addon, e.clientX, e.clientY);
        if (!hit) return;
        dragging.moved = true;
        var list = placements[dragging.addon.value] || [];
        if (hit.zone !== dragging.zone) {
          // Dragged into a different allowed zone — move it there unless the
          // zone is already taken by another pin of the same add-on.
          if (list.indexOf(hit.zone) !== -1) return;
          var at = list.indexOf(dragging.zone);
          if (at !== -1) list[at] = hit.zone;
          if (offsets[dragging.addon.value]) delete offsets[dragging.addon.value][dragging.zone];
          dragging.zone = hit.zone;
        }
        setPoint(dragging.addon.value, hit.zone, hit.x, hit.y);
        updateFlat();
      });

      var endDrag = function () {
        if (!dragging) return;
        dragging = null;
        updateFlat();
        updateMarkers();
      };
      flatWrap.addEventListener('pointerup', endDrag);
      flatWrap.addEventListener('pointercancel', endDrag);
      // A drag that ends on a zone would otherwise fire a click and toggle
      // the pin straight back off.
      flatWrap.addEventListener('click', function (e) {
        if (e.target.closest && e.target.closest('.hsf__pin')) {
          e.preventDefault();
          e.stopPropagation();
        }
      }, true);
    }

    // --- ZONE CASE — a finger-sized way into a flat drawn six times too small -
    // The jean's viewBox is 1800 units wide and lands at ~306px on a phone, so
    // the 87-unit coin pocket draws at 15px. Widening the snap tolerance cannot
    // rescue it: that tolerance is two pin-radii and PIN is a fixed 3.2% of the
    // viewBox width, so it is always ~20px of screen no matter what. The only
    // thing that actually grows a target is drawing it bigger, so these tiles
    // re-aim the viewBox at one band of the garment. The 34 zones, the hit test,
    // the offsets and the ticket contract are all untouched — only the view of
    // them changes. hs-customizer.css has carried the styles for this since the
    // first commit; nothing ever rendered them.
    // Touch and small screens only: a mouse is precise enough at full size, and
    // the approved desktop layout stays exactly as it is.
    var zoneCase = null, zoneCaseBack = null, zoneCaseFor = null;
    var ZONECASE_MQ = '(max-width: 899px), (pointer: coarse)';

    // Each face is a <g translate(dx,dy)>, so a zone's own x/y is panel-local.
    // Zooming needs the box in the svg's own coordinates.
    // Parsed by hand rather than by regex: this file carries ZERO backslash
    // literals because the GraphQL theme-file transport corrupts them, and every
    // escape a transform regex needs is a backslash.
    function panelOffset(g) {
      var t = g.getAttribute('transform') || '';
      var i = t.indexOf('translate(');
      if (i === -1) return { dx: 0, dy: 0 };
      var s = t.slice(i + 'translate('.length);
      var e = s.indexOf(')');
      if (e !== -1) s = s.slice(0, e);
      var parts = s.split(/[ ,]+/);
      return { dx: parseFloat(parts[0]) || 0, dy: parseFloat(parts[1]) || 0 };
    }
    function zoneStem(z) {
      var l = z.getAttribute('data-zone-label') || z.getAttribute('data-hsf-zone') || '';
      return String(l).split('—')[0].trim();
    }
    // Bands are read off the drawing, never hand-listed, so a garment flat that
    // did not exist when this was written still gets a working picker.
    function zoneBands(svg) {
      var out = [];
      Array.prototype.forEach.call(svg.children, function (g) {
        if (g.tagName !== 'g') return;
        var zs = Array.prototype.slice.call(g.querySelectorAll('[data-hsf-zone]'));
        if (!zs.length) return;
        var off = panelOffset(g);
        var items = zs.map(function (z) {
          var b = zoneBox(z);
          return { x: b.x + off.dx, y: b.y + off.dy, w: b.w, h: b.h, a: b.w * b.h, stem: zoneStem(z) };
        }).sort(function (p, q) { return (p.y + p.h / 2) - (q.y + q.h / 2); });
        var per = Math.ceil(items.length / Math.min(3, items.length));
        for (var i = 0; i < items.length; i += per) out.push(items.slice(i, i + per));
      });
      return out.filter(function (b) { return b.length; });
    }
    function bandBox(band) {
      var x1 = Infinity, y1 = Infinity, x2 = -Infinity, y2 = -Infinity;
      band.forEach(function (it) {
        x1 = Math.min(x1, it.x); y1 = Math.min(y1, it.y);
        x2 = Math.max(x2, it.x + it.w); y2 = Math.max(y2, it.y + it.h);
      });
      return { x: x1, y: y1, w: x2 - x1, h: y2 - y1 };
    }
    // Name a band after the largest things in it. "WAISTBAND / FRONT POCKET"
    // tells a customer where they are going; "BAND 1" does not.
    function bandName(band) {
      var seen = [];
      band.slice().sort(function (p, q) { return q.a - p.a; }).forEach(function (it) {
        if (it.stem && seen.length < 2 && seen.indexOf(it.stem) === -1) seen.push(it.stem);
      });
      return seen.join(' / ') || 'ZONES';
    }
    function zoomTo(box) {
      var svg = visibleSvg();
      if (!svg) return;
      if (!svg.getAttribute('data-hsf-vb0')) svg.setAttribute('data-hsf-vb0', svg.getAttribute('viewBox') || '');
      // Only the viewBox WIDTH sets the magnification — the svg is width-fitted
      // and its height follows — so padding is kept tight to buy real target size.
      var pad = Math.max(box.w, box.h) * 0.05 + 14;
      svg.setAttribute('viewBox',
        (box.x - pad) + ' ' + (box.y - pad) + ' ' + (box.w + pad * 2) + ' ' + (box.h + pad * 2));
      // PIN is derived from the viewBox width, so re-reading it here keeps the
      // pin graphic and the drag tolerance a constant size on screen.
      refreshScale();
      updateFlat();
      updateMarkers();
    }
    // WRITE [hidden] ONLY WHEN IT CHANGES. The zone case and its back button
    // live INSIDE flatWrap, and flatWrap is watched by a MutationObserver
    // filtered on [hidden]. Assigning el.hidden = true to an element that is
    // already hidden still queues a mutation record, so an unguarded write
    // re-enters the observer, which writes again — an unbounded microtask loop
    // that wedges the renderer. Measured at 5001 rebuilds from a single
    // external toggle before the probe capped it. This one guard is what makes
    // the observer safe; do not inline these assignments again.
    function setHidden(el, want) {
      if (el && el.hidden !== want) el.hidden = want;
    }
    function zoomOut() {
      var svg = visibleSvg();
      var vb0 = svg && svg.getAttribute('data-hsf-vb0');
      if (vb0) svg.setAttribute('viewBox', vb0);
      refreshScale();
      updateFlat();
      updateMarkers();
      setHidden(zoneCase, false);
      setHidden(zoneCaseBack, true);
    }
    function buildZoneCase() {
      if (!flatWrap) return;
      if (!zoneCase) {
        zoneCase = document.createElement('div');
        zoneCase.className = 'hsc__zonecase';
        zoneCaseBack = document.createElement('button');
        zoneCaseBack.type = 'button';
        zoneCaseBack.className = 'hsc__zonecase-back';
        zoneCaseBack.textContent = '← WHOLE GARMENT';
        zoneCaseBack.hidden = true;
        zoneCaseBack.addEventListener('click', zoomOut);
        var anchor = flatWrap.querySelector('[data-hsc-flat-active]');
        var at = (anchor && anchor.parentNode === flatWrap) ? anchor.nextSibling : flatWrap.firstChild;
        flatWrap.insertBefore(zoneCaseBack, at);
        flatWrap.insertBefore(zoneCase, zoneCaseBack.nextSibling);
      }
      var svg = visibleSvg();
      if (!svg || !(window.matchMedia && window.matchMedia(ZONECASE_MQ).matches)) {
        // This is the desktop path, and it is the one that used to spin: it runs
        // on EVERY observer callback, so an unguarded write here re-fired the
        // observer indefinitely and hung the page for mouse users.
        setHidden(zoneCase, true);
        setHidden(zoneCaseBack, true);
        return;
      }
      if (zoneCaseFor === svg && zoneCase.childNodes.length) return;
      zoneCaseFor = svg;
      zoneCase.innerHTML = '';
      zoneBands(svg).forEach(function (band) {
        var box = bandBox(band);
        var tile = document.createElement('button');
        tile.type = 'button';
        tile.className = 'hsc__zone-tile';
        var nm = document.createElement('span');
        nm.className = 'hsc__zone-name';
        nm.textContent = bandName(band);
        tile.appendChild(nm);
        tile.addEventListener('click', function () {
          zoomTo(box);
          setHidden(zoneCase, true);
          setHidden(zoneCaseBack, false);
        });
        zoneCase.appendChild(tile);
      });
      setHidden(zoneCase, false);
      setHidden(zoneCaseBack, true);
    }
    // Watched through BOTH the media query and a plain resize, because some
    // viewports (embedded panes, devtools overrides) resize without firing the
    // query. Acting only on a real change of state matters more than it looks:
    // iOS fires resize every time the URL bar collapses on scroll, and rebuilding
    // there would throw a customer out of the band they were working in.
    var zcLast = null, zcTimer = null;
    function syncZoneCase() {
      var on = !!(window.matchMedia && window.matchMedia(ZONECASE_MQ).matches);
      if (on === zcLast) return;
      zcLast = on;
      zoneCaseFor = null;
      zoomOut();
      buildZoneCase();
    }
    if (flatWrap) {
      buildZoneCase();
      zcLast = !!(window.matchMedia && window.matchMedia(ZONECASE_MQ).matches);
      if (window.matchMedia) {
        var zcMq = window.matchMedia(ZONECASE_MQ);
        if (zcMq.addEventListener) zcMq.addEventListener('change', syncZoneCase);
        else if (zcMq.addListener) zcMq.addListener(syncZoneCase);
      }
      window.addEventListener('resize', function () {
        clearTimeout(zcTimer);
        zcTimer = setTimeout(syncZoneCase, 150);
      });
      // The flat's own box is the most reliable signal of the three: it changes
      // whenever the layout actually changes, including in embedded panes that
      // never fire the window events, and it does NOT change when iOS merely
      // collapses the URL bar (that moves height, not the width of this column).
      if (window.ResizeObserver) {
        new ResizeObserver(function () {
          clearTimeout(zcTimer);
          zcTimer = setTimeout(syncZoneCase, 150);
        }).observe(flatWrap);
      }
      // The garment picker lives in another script and only flips [hidden] on a
      // face, so watch the DOM for that rather than reaching across into it.
      // Our OWN elements are skipped: they sit inside the observed subtree, so
      // reacting to them is how a rebuild becomes a feedback loop. setHidden()
      // already stops the same-value case; this stops the changed-value one too,
      // so neither half can restart the spin on its own.
      if (window.MutationObserver) {
        new MutationObserver(function (records) {
          var external = records.some(function (r) {
            var t = r.target;
            return t !== zoneCase && t !== zoneCaseBack
              && !(zoneCase && zoneCase.contains(t))
              && !(zoneCaseBack && zoneCaseBack.contains(t));
          });
          if (!external) return;
          if (visibleSvg() !== zoneCaseFor) { zoneCaseFor = null; buildZoneCase(); }
        }).observe(flatWrap, { attributes: true, subtree: true, attributeFilter: ['hidden'] });
      }
    }

    root.addEventListener('click', function (e) {
      var t = e.target;
      var btn = t.closest ? t.closest('[data-hsc-place-btn]') : null;
      if (btn) {
        activePlaceId = btn.getAttribute('data-hsc-place-btn');
        updateFlat();
        if (flatWrap) {
          flatWrap.scrollIntoView({ behavior: 'smooth', block: 'center' });
          flatWrap.classList.add('is-flash');
          setTimeout(function () { flatWrap.classList.remove('is-flash'); }, 1600);
        }
        return;
      }
      var chip = t.closest ? t.closest('[data-hsc-chip-zone]') : null;
      if (chip) {
        var chipAddon = chip.getAttribute('data-hsc-chip-addon');
        var chipZone = chip.getAttribute('data-hsc-chip-zone');
        var list = placements[chipAddon] || [];
        var ix = list.indexOf(chipZone);
        if (ix !== -1) list.splice(ix, 1);
        if (offsets[chipAddon]) delete offsets[chipAddon][chipZone];
        updateFlat();
        updateMarkers();
      }
    });

    addonInputs.forEach(function (input) {
      input.addEventListener('change', function () {
        var note = root.querySelector('[data-hsc-addon-note="' + input.value + '"]');
        if (note) note.hidden = !input.checked;
        if (input.checked) activePlaceId = input.value;
        else delete placements[input.value];
        updateSummary();
        updateFlat();
      });
    });
    // Toggling FREESTYLE changes what is drawn, not just what is submitted, so it
    // has to redraw the flat — otherwise the pins linger under a "shop chooses" ticket.
    Array.prototype.forEach.call(root.querySelectorAll('[data-hsc-freestyle]'), function (el) {
      el.addEventListener('change', function () { updateFlat(); updateMarkers(); });
    });
    if (variantInput && variantInput.tagName === 'SELECT') variantInput.addEventListener('change', updateSummary);
    updateSummary();

    // --- Placement markers + live spec sheet -------------------------------
    function zoneState(zone) {
      if (zone === 'ADDONS') {
        var n = selectedAddons().length;
        return { label: 'CUSTOM WORK', value: n ? n + ' SELECTED' : 'NONE', custom: n > 0 };
      }
      if (zone === 'THREAD — HEM') {
        var t = root.querySelector('[data-hsc-thread] input:checked');
        var tv = t ? t.value : 'STOCK — TONAL';
        return { label: 'HEM THREAD', value: tv, custom: tv.indexOf('STOCK') !== 0 };
      }
      var g = root.querySelector('[data-hsc-hw-group="' + zone + '"]');
      if (!g) return null;
      var c = g.querySelector('input:checked');
      var v = c ? c.value : 'STOCK — AS SHOWN';
      return { label: zone, value: v, custom: v.indexOf('STOCK') !== 0 };
    }

    var markers = Array.prototype.slice.call(root.querySelectorAll('[data-hsc-marker]'));
    var specWrap = root.querySelector('[data-hsc-spec]');
    var specRows = root.querySelector('[data-hsc-spec-rows]');

    function updateMarkers() {
      var anyCustom = false;
      var rowsHtml = '';
      markers.forEach(function (mk) {
        var zone = mk.getAttribute('data-zone');
        var st = zoneState(zone);
        if (!st) { mk.hidden = true; return; }
        mk.hidden = false;
        mk.textContent = st.custom ? st.label + ': ' + st.value : st.label;
        mk.classList.toggle('is-custom', st.custom);
        if (st.custom) anyCustom = true;
        rowsHtml += '<div class="hsc__spec-row' + (st.custom ? ' is-custom' : '') + '"><span>' + st.label + '</span><span>' + st.value + '</span></div>';
      });
      // One row per placed add-on: which work, where on the flat.
      selectedAddons().forEach(function (input) {
        if (isFreestyle(input.value)) {
          anyCustom = true;
          rowsHtml += '<div class="hsc__spec-row is-custom"><span>' + input.getAttribute('data-title') + '</span><span>FREESTYLE — SHOP CHOOSES</span></div>';
          return;
        }
        var zs = effectiveZones(input.value);
        if (!zs.length) return;
        anyCustom = true;
        rowsHtml += '<div class="hsc__spec-row is-custom"><span>' + input.getAttribute('data-title') + '</span><span>' + zs.map(zoneLabel).join(' + ') + '</span></div>';
      });
      if (specRows) specRows.innerHTML = rowsHtml;
      if (specWrap) specWrap.hidden = !anyCustom;
    }

    markers.forEach(function (mk) {
      mk.addEventListener('click', function () {
        var zone = mk.getAttribute('data-zone');
        var target = zone === 'ADDONS' ? root.querySelector('[data-hsc-addon]') :
          zone === 'THREAD — HEM' ? root.querySelector('[data-hsc-thread]') :
          root.querySelector('[data-hsc-hw-group="' + zone + '"]');
        if (!target) return;
        var group = target.closest('.hsc__group, .hsc__step') || target;
        group.scrollIntoView({ behavior: 'smooth', block: 'center' });
        group.classList.add('is-flash');
        setTimeout(function () { group.classList.remove('is-flash'); }, 1600);
      });
    });

    // Any selection change anywhere in the section refreshes markers + spec.
    root.addEventListener('change', updateMarkers);
    updateMarkers();
    updateFlat();

    // --- URL PREFILL + SHAREABLE BUILD -------------------------------------
    // Lets a SUNDAE combo on the parlor board be a plain link:
    //   ?variant=<id>&add=PATCHES,STUDS,GROMMETS&thread=GOLD
    // Also gives refresh-persistence and a shareable link.
    //
    // Skipped entirely in placementOnly mode: there hs-builder.js owns the same
    // [data-hsc-addon] inputs, so running here too would double-apply and the
    // two syncUrl writers would fight over the query string.
    if (!placementOnly) {
      var slugify = function (s) {
        return String(s == null ? '' : s).toLowerCase()
          .replace(/[^a-z0-9]+/g, '-').replace(/^-+/, '').replace(/-+$/, '');
      };
      var matchInput = function (inputs, token) {
        var t = slugify(token);
        if (!t) return null;
        var by = function (get) {
          return inputs.filter(function (i) {
            var v = slugify(get(i));
            return v && v === t;
          })[0] || null;
        };
        return by(function (i) { return i.value; })
          || by(function (i) { return i.getAttribute('data-sku'); })
          || by(function (i) { return i.getAttribute('data-title'); });
      };
      var fire = function (el) {
        if (el) el.dispatchEvent(new Event('change', { bubbles: true }));
      };
      var readQuery = function () {
        try { return new URLSearchParams(window.location.search); } catch (e) { return null; }
      };
      var threadInputs = Array.prototype.slice.call(
        root.querySelectorAll('[data-hsc-thread] input'));
      var OWNED = ['variant', 'add', 'thread'];

      var q0 = readQuery();
      if (q0) {
        var wantVariant = q0.get('variant');
        if (wantVariant && variantInput) {
          var wv = slugify(wantVariant);
          if (variantInput.tagName === 'SELECT') {
            var opt = Array.prototype.filter.call(variantInput.options, function (o) {
              return slugify(o.value) === wv;
            })[0];
            if (opt) { variantInput.value = opt.value; fire(variantInput); }
          }
        }
        (q0.get('add') || '').split(',').forEach(function (tok) {
          var a = matchInput(addonInputs, tok);
          if (a && !a.checked) { a.checked = true; fire(a); }
        });
        var th = matchInput(threadInputs, q0.get('thread'));
        if (th) { th.checked = true; fire(th); }
      }

      // Preserve every param we do not own - notably preview_theme_id, which
      // if dropped silently bounces the viewer onto the live theme.
      var syncUrl = function () {
        if (!window.history || !window.history.replaceState) return;
        var q = readQuery();
        if (!q) return;
        OWNED.forEach(function (k) { q.delete(k); });

        if (variantInput && variantInput.value) q.set('variant', variantInput.value);
        var add = addonInputs.filter(function (i) { return i.checked; }).map(function (a) {
          return slugify(a.getAttribute('data-title')) || a.value;
        });
        if (add.length) q.set('add', add.join(','));
        var tsel = threadInputs.filter(function (i) { return i.checked; })[0];
        if (tsel) q.set('thread', slugify(tsel.getAttribute('data-title')) || tsel.value);

        var qs = q.toString();
        try {
          window.history.replaceState(null, '', window.location.pathname
            + (qs ? '?' + qs : '') + window.location.hash);
        } catch (e) { /* non-fatal: the build still works, it is just not shareable */ }
      };
      root.addEventListener('change', syncUrl);
      syncUrl();
    }

    function collectProperties(ref, lead) {
      var props = {
        'ENTITY': cfg.entity.toUpperCase(),
        'ORDER TYPE': 'CUSTOMIZATION',
        'CUSTOMIZATION REF': ref
      };
      Array.prototype.forEach.call(root.querySelectorAll('[data-hsc-hw-group]'), function (group) {
        var checked = group.querySelector('input:checked');
        if (checked) props['HARDWARE — ' + group.getAttribute('data-hsc-hw-group')] = checked.value;
      });
      var thread = root.querySelector('[data-hsc-thread] input:checked');
      if (thread) props['THREAD / STITCH COLOUR'] = thread.value;

      // Generic sweep: anything in the section carrying data-hsc-prop becomes a
      // line item property named by that attribute. This is how the customer's
      // own-garment details ride along, and it means new fields can be added in
      // Liquid alone with no change here.
      Array.prototype.forEach.call(root.querySelectorAll('[data-hsc-prop]'), function (el) {
        var name = el.getAttribute('data-hsc-prop');
        if (!name) return;
        var val;
        if (el.type === 'checkbox') { val = el.checked ? 'YES' : ''; }
        else if (el.type === 'radio') { return; } // handled by the group sweep below
        else { val = (el.value || '').trim(); }
        if (val) props[name] = val;
      });
      // radio groups: take the checked one, keyed off the group wrapper
      Array.prototype.forEach.call(root.querySelectorAll('[data-hsc-prop-group]'), function (group) {
        var checked = group.querySelector('input:checked');
        if (checked) props[group.getAttribute('data-hsc-prop-group')] = checked.value;
      });

      props['ESTIMATED LEAD TIME'] = lead + ' BUSINESS DAYS';
      return props;
    }

    // Required own-garment fields must be filled before the order can go in —
    // we cannot draft work on a garment we know nothing about.
    function missingRequired() {
      return Array.prototype.filter.call(
        root.querySelectorAll('[data-hsc-prop][data-hsc-required]'),
        function (el) { return !(el.value || '').trim(); }
      );
    }

    if (placementOnly) addBtn = null;

    // Published so hs-builder.js can copy the marks onto its own cart lines —
    // placement that cannot reach the production ticket is worse than none.
    root.hsPlacementFor = function (addonId) {
      if (isFreestyle(addonId)) return { freestyle: true, zones: [], xy: '' };
      var zs = effectiveZones(addonId);
      return {
        freestyle: false,
        zones: zs.slice(),
        xy: zs.map(function (zid) {
          var p = pointAt(addonId, zid);
          return p ? zid + ':' + Math.round(p.x) + ',' + Math.round(p.y) : zid;
        }).join(';')
      };
    };

    // A correction is a change or a keystroke. In placement-only mode errEl is
    // null (the builder owns the message region there) and these are inert.
    root.addEventListener('change', clearError);
    root.addEventListener('input', clearError);

    // Only on the CUSTOMIZE page. In placement-only mode the host engine owns
    // these steps and reporting them here would double-count every one.
    if (track && !placementOnly) {
      track.step('start');
      root.addEventListener('change', function (e) {
        var t = e.target;
        if (t && t.hasAttribute && t.hasAttribute('data-hsc-addon')) track.step('work');
      });
      root.addEventListener('input', function (e) {
        var t = e.target;
        if (t && t.hasAttribute && t.hasAttribute('data-hsc-prop')) track.step('garment');
      });
    }

    addBtn && addBtn.addEventListener('click', function () {
      clearError();

      // Tell us about the garment first.
      var missing = missingRequired();
      if (missing.length) {
        showError('TELL US ABOUT YOUR GARMENT: ' + missing.map(function (el) {
          return el.getAttribute('data-hsc-prop');
        }).join(', '));
        missing[0].focus();
        missing[0].scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }

      // Add-ons with configured placement rules must be placed on the flat
      // before the order goes in — the ticket needs marks, not guesses.
      // FREESTYLE means the customer is handing the decision to the shop, so it
      // satisfies the placement requirement instead of blocking the order.
      var unplaced = selectedAddons().filter(function (i) {
        return placementCfg(i) && !isFreestyle(i.value) && !(placements[i.value] || []).length;
      });
      if (unplaced.length) {
        showError('TAP THE FLAT TO PLACE: ' + unplaced.map(function (i) { return i.getAttribute('data-title'); }).join(', '));
        activePlaceId = unplaced[0].value;
        updateFlat();
        if (flatWrap) {
          flatWrap.scrollIntoView({ behavior: 'smooth', block: 'center' });
          flatWrap.classList.add('is-flash');
          setTimeout(function () { flatWrap.classList.remove('is-flash'); }, 1600);
        }
        return;
      }

      track && track.step('submit');

      addBtn.disabled = true;
      addBtn.textContent = 'ADDING…';

      var lead = updateSummary();
      var ref = 'CUST-' + Date.now().toString(36).toUpperCase();
      var baseVariantId = parseInt(variantInput.value, 10);
      var items = [];

      items.push({
        id: baseVariantId,
        quantity: 1,
        properties: collectProperties(ref, lead)
      });

      selectedAddons().forEach(function (input) {
        var note = root.querySelector('[data-hsc-addon-note="' + input.value + '"]');
        var props = {
          'ENTITY': cfg.entity.toUpperCase(),
          'ORDER TYPE': 'CUSTOMIZATION ADD-ON',
          'CUSTOMIZATION REF': ref,
          'APPLIED TO': cfg.baseTitle + (cfg.baseSku ? ' [' + cfg.baseSku + ']' : '')
        };
        var zs = placements[input.value] || [];
        if (isFreestyle(input.value)) {
          props['PLACEMENT'] = 'FREESTYLE — SHOP CHOOSES';
          props['_PLACEMENT_ZONES'] = 'FREESTYLE';
        } else if (zs.length) {
          props['PLACEMENT'] = zs.map(zoneLabel).join(' + ');
          // Machine-readable zone ids for the ticket's flat drawing.
          // Leading underscore hides it from theme cart/checkout displays.
          props['_PLACEMENT_ZONES'] = zs.join(',');
          // Exact dragged point per zone, in flat user units, so the ticket
          // can print the pin where the customer actually put it.
          props['_PLACEMENT_XY'] = zs.map(function (zid) {
            var p = pointAt(input.value, zid);
            return p ? zid + ':' + Math.round(p.x) + ',' + Math.round(p.y) : zid;
          }).join(';');
        }
        if (note && note.value.trim()) props['DETAILS / NOTES'] = note.value.trim().toUpperCase();
        items.push({ id: parseInt(input.value, 10), quantity: 1, properties: props });
      });

      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: items })
      })
        .then(function (r) {
          if (!r.ok) return r.json().then(function (j) { throw new Error(j.description || j.message || 'CART ERROR'); });
          return r.json();
        })
        .then(function () {
          // Cart attribute mirrors the entity for order-level reporting.
          return fetch('/cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ attributes: { entity: cfg.entity } })
          });
        })
        .then(function () {
          document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
          // Stay put. Navigating to /cart drops the ?preview_theme_id
          // session, which dumped the customer on the live theme's cart and
          // made the order look like it had vanished. The tray below shows
          // what was added and lets them remove it.
          document.dispatchEvent(new CustomEvent('hsdb:cart-changed', { bubbles: true }));
          if (track) {
            track.step('added');
            track.event('hs_add_to_cart', { ref: ref, lines: items.length, lead_days: lead });
          }
          clearError();
          addBtn.disabled = false;
          addBtn.textContent = 'ADDED ✓ — ADD ANOTHER';
          var tray = document.querySelector('[data-hsdb-tray]');
          if (tray) tray.scrollIntoView({ behavior: 'smooth', block: 'center' });
        })
        .catch(function (e) {
          showError((e.message || 'SOMETHING BROKE. TRY AGAIN.').toUpperCase());
          addBtn.disabled = false;
          addBtn.textContent = 'ADD TO ORDER';
        });
    });
  }

  function boot() {
    Array.prototype.forEach.call(document.querySelectorAll('.hsc'), initSection);
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
  document.addEventListener('shopify:section:load', function (e) {
    var el = e.target.querySelector('.hsc');
    if (el) initSection(el);
  });
})();
